import { db, auth } from '../firebase/config';
import { appendActivity } from './workspaceService';
import { uploadBackup } from './backupService';
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  Timestamp,
  writeBatch,
  runTransaction
} from 'firebase/firestore';

const COLLECTION = 'closingStatements';

// Estado de firma del Closing Statement (ver claim_lifecycle_restructure.md):
// - sin_firma: pendiente, aún no se sube el PDF firmado (default para todo
//   Closing Statement nuevo).
// - con_firma: el PDF firmado ya fue subido — solo entonces puede avanzar
//   más allá de "pending_signature".
// - legado: EXCLUSIVO de reclamos migrados de antes de esta restructuración
//   (ver Fase 1B) — nunca se asigna a un Closing Statement creado después.
//   No bloquea nada; el PDF real se puede adjuntar después si aparece, pero
//   es opcional, nunca obligatorio para este estado.
export const SIGNATURE_STATUSES = ['sin_firma', 'con_firma', 'legado'];

const currentEmail = () => auth.currentUser?.email || 'system@csrclaims.com';

export const getClosingStatements = async () => {
  const snapshot = await getDocs(query(collection(db, COLLECTION), orderBy('createdAt', 'desc')));
  return snapshot.docs.map(item => ({ id: item.id, ...item.data() }));
};

// Un check solo guarda `closingStatementId` (ver createClaimFromStatement en
// claimService.js) — el PDF firmado en sí vive en el documento del
// closingStatement (signedPdf.storageKey, ver attachSignedPdf), así que el
// portal de ajustadores (ClaimsDirectory.jsx, solo lectura) necesita esto
// para poder abrirlo bajo demanda sin tener que desnormalizar el storageKey
// hacia cada check (que dejaría a los ~155 cheques ya existentes sin el
// botón, igual que pasó con Aseguradora/Email/Teléfono).
export const getClosingStatementById = async id => {
  if (!id) return null;
  const snapshot = await getDoc(doc(db, COLLECTION, id));
  return snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null;
};

export const createClosingStatement = async data => {
  const now = Timestamp.now();
  const payload = {
    ...data,
    status: data.status || 'draft',
    signatureStatus: SIGNATURE_STATUSES.includes(data.signatureStatus) ? data.signatureStatus : 'sin_firma',
    signatureDate: data.signatureDate || null,
    signedPdf: null,
    createdAt: now,
    updatedAt: now,
    createdBy: currentEmail(),
    notifiedAt: null,
    signedAt: null,
    linkedClaimId: null
  };
  const ref = doc(collection(db, COLLECTION));
  const batch = writeBatch(db);
  batch.set(ref, payload);
  appendActivity(batch, { event: 'statement_created', targetId: ref.id, reference: data.claimNumber || ref.id });
  await batch.commit();
  return { id: ref.id, ...payload };
};

export const updateClosingStatement = async (id, updates) => {
  const payload = { ...updates, updatedAt: Timestamp.now() };
  if (updates.status === 'notified') payload.notifiedAt = Timestamp.now();
  if (updates.status === 'signed') payload.signedAt = Timestamp.now();
  await runTransaction(db, async transaction => {
    const ref = doc(db, COLLECTION, id);
    const snapshot = await transaction.get(ref);
    if (!snapshot.exists()) throw new Error('Closing statement not found');
    const current = snapshot.data();
    // Gate: no se puede pasar de "pending_signature" a "signed" sin el PDF
    // firmado adjunto — salvo estatus "legado" (exclusivo de reclamos
    // migrados antes de esta restructuración, ver claim_lifecycle_restructure.md).
    if (updates.status === 'signed') {
      const signatureStatus = updates.signatureStatus || current.signatureStatus;
      const hasSignedPdf = Boolean((updates.signedPdf || current.signedPdf)?.storageKey);
      if (signatureStatus !== 'legado' && !hasSignedPdf) {
        throw new Error('SIGNED_PDF_REQUIRED');
      }
    }
    transaction.update(ref, payload);
    if (updates.status && updates.status !== current.status) {
      appendActivity(transaction, { event: 'statement_status', targetId: id, reference: current.claimNumber || id, status: updates.status });
    }
  });
  return { id, ...payload };
};

// Sube el PDF firmado a Hostinger (mismo mecanismo que los demás backups —
// ver backupService.js/uploadBackup) y lo adjunta al Closing Statement,
// marcando signatureStatus como 'con_firma'. No cambia el status del
// statement por sí sola — eso lo decide quien llama, vía updateClosingStatement.
export const attachSignedPdf = async (id, file, signatureDate) => {
  const backup = await uploadBackup({ file, claimId: id, invoiceNumber: '', type: 'closing_statement_signature' });
  const signedPdf = {
    storageKey: backup.storageKey,
    name: backup.name,
    mimeType: backup.mimeType,
    size: backup.size,
    uploadedAt: Timestamp.now(),
    uploadedBy: backup.uploadedBy || currentEmail()
  };
  const ref = doc(db, COLLECTION, id);
  const snapshot = await getDoc(ref);
  if (!snapshot.exists()) throw new Error('Closing statement not found');
  const payload = {
    signedPdf,
    signatureStatus: 'con_firma',
    signatureDate: signatureDate || null,
    updatedAt: Timestamp.now()
  };
  await runTransaction(db, async transaction => {
    transaction.update(ref, payload);
  });
  return { id, ...snapshot.data(), ...payload };
};

export const deleteClosingStatement = id => deleteDoc(doc(db, COLLECTION, id));
